import { dateSortKey, dateYear, formatDate } from './dates';
import type { EventType, Gender, GenderIdentity, Id, LifeEvent, Person, PersonName, Relationship, TermStyle, Tree } from './types';

export function newId(prefix = ''): string {
  const rand = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
    : Math.random().toString(36).slice(2, 14);
  return prefix + rand;
}

export function emptyName(): PersonName {
  return { given: '', surname: '', type: 'name' };
}

export function newPerson(partial: Partial<Person> = {}): Person {
  return {
    id: newId('p'),
    names: [emptyName()],
    gender: { identity: 'unknown' },
    living: null,
    events: [],
    notes: '',
    tags: [],
    ...partial,
  };
}

export function newEvent(type: EventType, partial: Partial<LifeEvent> = {}): LifeEvent {
  return { id: newId('e'), type, ...partial };
}

/** Hidden names, and every deadname, never appear in the app, search, or default exports. */
export function isHiddenName(n: PersonName): boolean {
  return !!n.hidden || n.type === 'deadname';
}

/** The first non-hidden name, which is what we show everywhere. */
export function primaryName(p: Person): PersonName | undefined {
  return p.names.find((n) => !isHiddenName(n)) ?? undefined;
}

export function visibleNames(p: Person): PersonName[] {
  return p.names.filter((n) => !isHiddenName(n));
}

export function nameToString(n: PersonName | undefined, opts: { nickname?: boolean } = {}): string {
  if (!n) return '';
  const parts = [n.prefix, n.given, opts.nickname && n.nickname ? `“${n.nickname}”` : '', n.surname, n.suffix];
  return parts.filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
}

export function displayName(p: Person | undefined): string {
  if (!p) return 'Unknown person';
  const n = primaryName(p);
  const s = nameToString(n);
  return s || 'Unnamed person';
}

export function shortName(p: Person | undefined): string {
  if (!p) return '?';
  const n = primaryName(p);
  if (!n) return 'Unnamed';
  return n.nickname || n.given.split(' ')[0] || n.surname || 'Unnamed';
}

export function initials(p: Person): string {
  const n = primaryName(p);
  if (!n) return '?';
  // By character, not UTF-16 unit, so a name starting with an emoji or a rare character isn't cut in half.
  const a = [...(n.given || n.nickname || '').trim()][0] ?? '';
  const b = [...(n.surname || '').trim()][0] ?? '';
  return (a + b).toUpperCase() || '?';
}

export function findEvent(p: Person, type: EventType): LifeEvent | undefined {
  return p.events.find((e) => e.type === type);
}

/** The event that records someone's death: a death, else a burial or cremation. */
export function deathEvent(p: Person): LifeEvent | undefined {
  return findEvent(p, 'death') ?? findEvent(p, 'burial') ?? findEvent(p, 'cremation');
}

/** The most recent event of a type: latest dated one, else the last one entered. */
export function latestEvent(p: Person, type: 'occupation' | 'residence'): LifeEvent | undefined {
  const all = p.events.filter((e) => e.type === type);
  const dated = all.filter((e) => e.date).sort((a, b) => (dateSortKey(a.date) < dateSortKey(b.date) ? -1 : 1));
  return dated.at(-1) ?? all.at(-1);
}

/**
 * Set a field on the person's birth or death event (or latest job or home), creating or dropping
 * the event as needed. A death date or place goes on a death event, never on the burial or
 * cremation that stands in for it when there's no death record.
 */
export function withEventField(p: Person, which: 'birth' | 'death' | 'occupation' | 'residence', field: 'date' | 'place' | 'label', value: string | undefined): Person {
  if (which === 'death' && !findEvent(p, 'death') && (value || undefined) === (deathEvent(p)?.[field] || undefined)) return p;
  const existing = which === 'birth' || which === 'death' ? findEvent(p, which) : latestEvent(p, which);
  if (!existing) {
    if (!value) return p;
    return { ...p, events: [...p.events, newEvent(which, { [field]: value })] };
  }
  const next = { ...existing, [field]: value || undefined };
  const empty = !next.date && !next.place && !next.note && !next.label && !next.passthrough?.length;
  return { ...p, events: empty ? p.events.filter((e) => e.id !== existing.id) : p.events.map((e) => (e.id === existing.id ? next : e)) };
}

export function birthDate(p: Person): string | undefined {
  return findEvent(p, 'birth')?.date;
}

export function deathDate(p: Person): string | undefined {
  return findEvent(p, 'death')?.date ?? findEvent(p, 'burial')?.date ?? findEvent(p, 'cremation')?.date;
}

/** Living, deceased, or not recorded (null `living` with nothing in the dates to say). */
export type LifeStatus = 'living' | 'deceased' | 'unknown';

export const LIFE_STATUS_LABELS: Record<LifeStatus, string> = { living: 'Living', deceased: 'Deceased', unknown: 'Not recorded' };

/**
 * Whether someone's own records show they have died: a death, burial, or cremation; a birth over
 * 110 years ago; or, with no birth date, a dated event (or one of `otherYears`, such as a
 * marriage) over 100 years ago.
 */
export function presumedDeceased(p: Person, otherYears: number[] = [], now = new Date()): boolean {
  if (p.events.some((e) => e.type === 'death' || e.type === 'burial' || e.type === 'cremation')) return true;
  const year = now.getFullYear();
  const by = dateYear(birthDate(p));
  if (by !== undefined) return year - by > 110;
  const years = [...p.events.map((e) => dateYear(e.date)), ...otherYears].filter((y): y is number => y !== undefined);
  return years.length > 0 && year - Math.min(...years) > 100;
}

/**
 * For imports: mark people deceased when their records show it, counting marriages and other
 * partnership dates too. Everyone else keeps what the file said, or stays not recorded.
 */
export function markDeceasedFromDates(people: Person[], relationships: Relationship[], now = new Date()) {
  const relYears = new Map<Id, number[]>();
  for (const r of relationships) {
    const y = r.kind === 'partner' ? dateYear(r.start) : undefined;
    if (y === undefined) continue;
    for (const id of [r.a, r.b]) relYears.set(id, [...(relYears.get(id) ?? []), y]);
  }
  for (const p of people) if (p.living === null && presumedDeceased(p, relYears.get(p.id), now)) p.living = false;
}

/** A choice made in the editor (or a file) wins; otherwise the dates decide, or it's not recorded. */
export function lifeStatus(p: Person): LifeStatus {
  if (p.living === true) return 'living';
  if (p.living === false) return 'deceased';
  return presumedDeceased(p) ? 'deceased' : 'unknown';
}

/** Recorded as living. People whose status isn't recorded are not included. */
export function isLiving(p: Person): boolean {
  return lifeStatus(p) === 'living';
}

/** Recorded as deceased, or their dates show it. Ages and birthdays are shown for everyone else. */
export function isDeceased(p: Person): boolean {
  return lifeStatus(p) === 'deceased';
}

/** "1850–1921", "b. 1990", "d. 1921", or "". */
export function lifeSpan(p: Person): string {
  const b = dateYear(birthDate(p));
  const d = dateYear(deathDate(p));
  const dead = isDeceased(p);
  if (b !== undefined && d !== undefined) return `${b}–${d}`;
  if (b !== undefined) return dead ? `${b}–?` : `b. ${b}`;
  if (d !== undefined) return `d. ${d}`;
  return dead ? 'deceased' : '';
}

export function lifeSpanLong(p: Person): string {
  const b = formatDate(birthDate(p));
  const d = formatDate(deathDate(p));
  if (b && d) return `${b} – ${d}`;
  if (b) return `Born ${b}`;
  if (d) return `Died ${d}`;
  return '';
}

/** Pronouns filled in when someone's gender is set to man or woman and none are recorded yet. */
const DEFAULT_PRONOUNS: Partial<Record<GenderIdentity, string>> = {
  woman: 'she/her',
  'trans-woman': 'she/her',
  man: 'he/him',
  'trans-man': 'he/him',
};

export function defaultPronouns(identity: GenderIdentity): string | undefined {
  return DEFAULT_PRONOUNS[identity];
}

export const GENDER_LABELS: Record<GenderIdentity, string> = {
  woman: 'Woman',
  man: 'Man',
  nonbinary: 'Nonbinary',
  'trans-woman': 'Trans woman',
  'trans-man': 'Trans man',
  transfeminine: 'Transfeminine',
  transmasculine: 'Transmasculine',
  genderfluid: 'Genderfluid',
  genderqueer: 'Genderqueer',
  custom: 'Self-described',
  unknown: 'Unknown',
};

export function genderLabel(g: Gender): string {
  if (g.identity === 'custom') return g.label?.trim() || 'Self-described';
  return GENDER_LABELS[g.identity] ?? 'Unknown';
}

export type TermGender = 'feminine' | 'masculine' | 'neutral';

const GENDER_TERMS: Partial<Record<GenderIdentity, TermGender>> = {
  woman: 'feminine',
  'trans-woman': 'feminine',
  transfeminine: 'feminine',
  man: 'masculine',
  'trans-man': 'masculine',
  transmasculine: 'masculine',
};

/** Gendered words a set of pronouns implies, from the first set listed ("she/they" → feminine). */
export function pronounTerms(pronouns: string | undefined): TermGender | undefined {
  const pr = (pronouns ?? '').trim().toLowerCase();
  if (!pr) return undefined;
  if (/^any\b|^all\b/.test(pr)) return 'neutral';
  const first = pr.split(/[\/,\s]+/)[0];
  if (first === 'she' || first === 'her') return 'feminine';
  if (first === 'he' || first === 'him') return 'masculine';
  return 'neutral';
}

/**
 * Which relationship words describe someone (mother / father / parent). A choice the person's
 * record makes wins; otherwise pronouns decide, then gender, then neutral.
 */
export function termGender(p: Person | undefined): TermGender {
  return termGenderWithReason(p).terms;
}

export function termGenderWithReason(p: Person | undefined): { terms: TermGender; reason: 'chosen' | 'pronouns' | 'gender' | 'default' } {
  if (!p) return { terms: 'neutral', reason: 'default' };
  const chosen: TermStyle = p.terms ?? 'auto';
  if (chosen !== 'auto') return { terms: chosen, reason: 'chosen' };
  const fromPronouns = pronounTerms(p.pronouns);
  if (fromPronouns) return { terms: fromPronouns, reason: 'pronouns' };
  const fromGender = GENDER_TERMS[p.gender.identity];
  if (fromGender) return { terms: fromGender, reason: 'gender' };
  return { terms: 'neutral', reason: 'default' };
}

/** Pronoun forms used in generated sentences. Falls back to they/them. */
export function pronounSet(p: Person): { subj: string; obj: string; poss: string } {
  // The first set listed, by its first word ("her/hers" is she, not he).
  const first = (p.pronouns ?? '').trim().toLowerCase().split(/[\/,\s]+/)[0];
  if (first === 'she' || first === 'her' || first === 'hers') return { subj: 'she', obj: 'her', poss: 'her' };
  if (first === 'he' || first === 'him' || first === 'his') return { subj: 'he', obj: 'him', poss: 'his' };
  if (first === 'xe' || first === 'xem' || first === 'xyr') return { subj: 'xe', obj: 'xem', poss: 'xyr' };
  if (first === 'ze' || first === 'zie' || first === 'hir') return { subj: 'ze', obj: 'hir', poss: 'hir' };
  return { subj: 'they', obj: 'them', poss: 'their' };
}

export function sortedPeople(tree: Tree): Person[] {
  return Object.values(tree.people).sort(comparePeople);
}

/** Alphabetical as people read it: "Álvarez" with the A's, not after "Zhang". */
const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

export function comparePeople(a: Person, b: Person): number {
  const na = primaryName(a);
  const nb = primaryName(b);
  return (
    collator.compare(na?.surname || '', nb?.surname || '') ||
    collator.compare(na?.given || '', nb?.given || '') ||
    (dateYear(birthDate(a)) ?? 0) - (dateYear(birthDate(b)) ?? 0)
  );
}

export const EVENT_LABELS: Record<EventType, string> = {
  birth: 'Birth',
  death: 'Death',
  burial: 'Burial',
  cremation: 'Cremation',
  residence: 'Residence',
  education: 'Education',
  graduation: 'Graduation',
  occupation: 'Occupation',
  military: 'Military service',
  immigration: 'Immigration',
  emigration: 'Emigration',
  naturalization: 'Naturalization',
  census: 'Census',
  retirement: 'Retirement',
  adoption: 'Adoption',
  'name-change': 'Name change',
  'gender-transition': 'Transition',
  'hrt-start': 'Started HRT',
  'coming-out': 'Coming out',
  custom: 'Other event',
};

export function eventTitle(e: LifeEvent): string {
  if (e.type === 'custom') return e.label?.trim() || 'Event';
  if (e.type === 'occupation' && e.label) return `Occupation: ${e.label}`;
  if (e.type === 'education' && e.label) return `Education: ${e.label}`;
  return EVENT_LABELS[e.type];
}
